import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { NotificationIcon } from '../../../helpers/icons';
import { Toaster } from '../../../helpers/toaster';
import NotificationItem from '../../../pages/ProtectedPages/SystemNotifications/NotificationItem';
import {
  getDownloadJobUrl,
  markAllNotificationsRead,
  markNotificationRead,
  systemNotificationsList,
  systemNotificationsUnreadCount,
} from '../../../pages/ProtectedPages/SystemNotifications/actions';
import {
  ACTIVE_JOB_POLL_INTERVAL_MS,
  UNREAD_POLL_INTERVAL_MS,
} from '../../../pages/ProtectedPages/SystemNotifications/constants';

const PANEL_UNREAD_LIMIT = 3;

/**
 * Header inbox: unread badge plus the newest unread notifications. It is the
 * place a queued download comes back to, so a finished job offers "Download
 * here" rather than only announcing itself. Read rows are deliberately absent
 * here — "View all notifications" is where the full history lives.
 */
const NotificationBell = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const containerRef = useRef(null);

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['systemNotificationsUnreadCount'],
    queryFn: systemNotificationsUnreadCount,
    select: (response) => Number(response?.data?.data?.[0]?.count || 0),
    refetchInterval: UNREAD_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['systemNotificationsPanel'],
    queryFn: () =>
      systemNotificationsList({
        page: 1,
        length: PANEL_UNREAD_LIMIT,
        unread_only: true,
      }),
    // Sliced again on the client so the panel keeps its shape even if the API
    // ever honours `length` loosely.
    select: (response) =>
      (response?.data?.data ?? []).slice(0, PANEL_UNREAD_LIMIT),
    // Only fetched while the panel is open; the badge above is what keeps the
    // header live the rest of the time.
    enabled: isOpen,
  });

  // A job that is still building is the one case where the user is actively
  // waiting, so the panel refreshes quickly until nothing is in flight.
  const hasActiveJob = useMemo(
    () =>
      notifications.some((notification) =>
        ['pending', 'in_progress'].includes(notification?.status),
      ),
    [notifications],
  );

  useEffect(() => {
    if (!isOpen || !hasActiveJob) return undefined;

    const timer = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['systemNotificationsPanel'] });
      queryClient.invalidateQueries({
        queryKey: ['systemNotificationsUnreadCount'],
      });
    }, ACTIVE_JOB_POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [isOpen, hasActiveJob, queryClient]);

  // The panel is an overlay rather than a page, so it closes on any click that
  // lands outside it.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onDocumentClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [isOpen]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['systemNotificationsPanel'] });
    queryClient.invalidateQueries({
      queryKey: ['systemNotificationsUnreadCount'],
    });
    queryClient.invalidateQueries({ queryKey: ['systemNotificationsList'] });
  }, [queryClient]);

  const { mutate: markRead } = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: refresh,
  });

  const { mutate: markAllRead, isPending: isMarkingAll } = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: refresh,
  });

  /**
   * Fetches a signed URL and hands it to the browser. The archive is served
   * straight from Wasabi, so nothing is streamed through the API and the tab
   * is never blocked by a large file.
   */
  const handleDownload = useCallback(
    async (notification) => {
      const downloadJobId = notification?.download?.id;
      if (!downloadJobId || downloadingId) return;

      setDownloadingId(downloadJobId);
      try {
        const response = await getDownloadJobUrl({ id: downloadJobId });
        const url = response?.data?.data?.[0]?.url;

        if (!url) {
          Toaster.error('This download is no longer available.');
          return;
        }

        window.location.assign(url);

        if (!notification?.is_read) {
          markRead({ id: notification.id });
        }
      } catch (_err) {
        // The interceptor has already surfaced the reason.
      } finally {
        setDownloadingId(null);
      }
    },
    [downloadingId, markRead],
  );

  const handleOpen = useCallback(
    (notification) => {
      if (!notification?.is_read) {
        markRead({ id: notification.id });
      }
      if (notification?.action_url) {
        setIsOpen(false);
        navigate(notification.action_url);
      }
    },
    [markRead, navigate],
  );

  return (
    <div className="position--relative" ref={containerRef}>
      <div
        className="position--relative c--pointer"
        data-tooltip="Notifications"
        tooltip-position="left"
      >
        <span
          className="text--primary bg--primary-50 p--sm radius--full w-min--36 w-max--36 h-min--36 h-max--36 d--flex align-items--center justify-content--center"
          onClick={() => setIsOpen((open) => !open)}
        >
          <NotificationIcon width={20} height={20} />
        </span>
        {unreadCount > 0 && (
          <div className="count-badge position--absolute top--0 right---5 font--xs font--600 bg--danger text--white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}
      </div>

      {isOpen && (
        <div className="position--absolute right--0 top--50 w-min--400 w-max--400 bg--white radius--sm box-shadow--lg border-full--black-100 overflow--hidden z-index--lg">
          <div className="d--flex align-items--center justify-content--between gap--sm p--sm border-bottom--black-100">
            <div className="d--flex align-items--center gap--xs">
              <span className="font--sm font--600 text--black">
                Notifications
              </span>
              {unreadCount > 0 && (
                <span className="count-badge font--xs font--600 bg--danger text--white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <div className="d--flex gap--sm align-items--center white-space--nowrap">
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="font--xs text--primary c--pointer bg--transparent"
                  disabled={isMarkingAll}
                  onClick={() => markAllRead()}
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          <div className="h-max--400 overflow-y--auto">
            {isLoading && (
              <div className="p--lg font--2xs text--gray text--c">
                Loading...
              </div>
            )}

            {!isLoading && !notifications.length && (
              <div className="p--lg font--2xs text--gray text--c">
                Nothing unread.
              </div>
            )}

            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                compact
                isDownloading={downloadingId === notification?.download?.id}
                onDownload={handleDownload}
                onOpen={handleOpen}
              />
            ))}
          </div>

          {/* Kept outside the scroll area so it stays reachable however long
              the list is. */}
          <div className="p--sm border-top--black-100 d--flex justify-content--center">
            <button
              type="button"
              className="font--xs font--600 text--primary c--pointer bg--transparent"
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications-inbox');
              }}
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
